import { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { COMPANY } from "@/lib/constants";

export default function LogoShowcase() {
    const reduce = useReducedMotion();
    const cardRef = useRef(null);
    const [tilt, setTilt] = useState({ x: 0, y: 0, scale: 1 });
    const [hover, setHover] = useState(false);

    const onMouseMove = (e) => {
        if (reduce || !cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width; // 0..1
        const py = (e.clientY - rect.top) / rect.height; // 0..1
        const rotY = (px - 0.5) * 22; // -11..11 deg
        const rotX = (0.5 - py) * 22;
        setTilt({ x: rotX, y: rotY, scale: 1.02, px, py });
    };

    const onLeave = () => {
        setTilt({ x: 0, y: 0, scale: 1 });
        setHover(false);
    };

    const px = tilt.px ?? 0.5;
    const py = tilt.py ?? 0.5;

    return (
        <section
            id="brand-showcase"
            data-testid="logo-showcase-section"
            className="relative py-20 sm:py-24 lg:py-28 border-t border-white/[0.06] overflow-hidden"
        >
            {/* ambient grid */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                    maskImage:
                        "radial-gradient(ellipse at center, black 30%, transparent 70%)",
                }}
            />

            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-5 order-2 lg:order-1"
                    >
                        <p className="text-xs font-mono uppercase tracking-[0.22em] text-[#7BD3FF]">
                            Marke
                        </p>
                        <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                            Eine Marke,
                            <br />
                            <span className="text-chrome">die für Qualität steht.</span>
                        </h2>
                        <p className="mt-5 text-[#C9D3E1] text-base leading-relaxed max-w-lg">
                            Alpen Reinigung verbindet alpine Bodenständigkeit mit
                            einem Premium-Anspruch. Bewegen Sie die Maus über die
                            Karte – das Logo reagiert in 3D auf Ihre Geste.
                        </p>

                        <ul className="mt-8 space-y-3">
                            {[
                                "Sauberkeit auf höchstem Niveau",
                                "Qualität, die man sieht und spürt",
                                "Zuverlässigkeit – Wort halten ist selbstverständlich",
                            ].map((line, i) => (
                                <li
                                    key={line}
                                    className="flex items-start gap-3 text-sm text-[#F4F7FB]"
                                >
                                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-md border border-[rgba(46,168,255,0.3)] bg-[rgba(46,168,255,0.08)] font-mono text-[10px] text-[#7BD3FF]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {line}
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    <motion.div
                        initial={reduce ? false : { opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.7, delay: 0.05 }}
                        className="lg:col-span-7 order-1 lg:order-2"
                    >
                        <div
                            className="relative mx-auto max-w-xl"
                            style={{ perspective: "1400px" }}
                        >
                            {/* glow underlay */}
                            <div
                                aria-hidden="true"
                                className="absolute -inset-6 rounded-[40px] blur-3xl"
                                style={{
                                    background:
                                        "radial-gradient(60% 60% at 50% 50%, rgba(46,168,255,0.22), transparent 70%)",
                                    opacity: hover ? 0.9 : 0.5,
                                    transition:
                                        "opacity 400ms ease, background 400ms ease",
                                }}
                            />

                            <div
                                ref={cardRef}
                                onMouseEnter={() => setHover(true)}
                                onMouseMove={onMouseMove}
                                onMouseLeave={onLeave}
                                className="relative rounded-[28px] border border-white/[0.10] bg-gradient-to-br from-[#0E141F] via-[#0B0F16] to-[#07090C] card-elevated"
                                style={{
                                    transformStyle: "preserve-3d",
                                    transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${tilt.scale})`,
                                    transition:
                                        "transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)",
                                }}
                                data-testid="logo-3d-hover-card"
                            >
                                {/* metallic frame */}
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-0 rounded-[28px]"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(126,140,163,0.10) 100%)",
                                    }}
                                />

                                {/* corner badges */}
                                <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                                    <span className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-white/[0.04] border border-white/[0.10] text-[10px] font-mono uppercase tracking-[0.18em] text-[#7BD3FF]">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#2EA8FF] animate-pulse" />
                                        Alpen Reinigung
                                    </span>
                                </div>
                                <div className="absolute top-4 right-4 z-10">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8FA0B8]">
                                        EST. Innsbruck
                                    </span>
                                </div>

                                {/* logo */}
                                <div
                                    className="relative px-4 sm:px-6 pt-12 sm:pt-14 pb-8 flex items-center justify-center"
                                    style={{ transform: "translateZ(40px)" }}
                                >
                                    <div className="relative w-full max-w-md rounded-2xl overflow-hidden border border-white/[0.10] bg-black">
                                        <img
                                            src={COMPANY.logo}
                                            alt="Alpen Reinigung Logo"
                                            width={1024}
                                            height={1024}
                                            decoding="async"
                                            fetchpriority="high"
                                            loading="eager"
                                            draggable={false}
                                            className="block w-full h-auto select-none"
                                            style={{
                                                imageRendering: "auto",
                                                WebkitBackfaceVisibility: "hidden",
                                                backfaceVisibility: "hidden",
                                                transform: "translateZ(0)",
                                            }}
                                        />
                                        {/* soft inner blue glow on top of image (does not blur the logo itself) */}
                                        <div
                                            aria-hidden="true"
                                            className="pointer-events-none absolute inset-0 rounded-2xl"
                                            style={{
                                                boxShadow:
                                                    "inset 0 0 60px rgba(46,168,255,0.18), inset 0 0 0 1px rgba(255,255,255,0.04)",
                                            }}
                                        />
                                    </div>

                                    {/* shine sweep */}
                                    <div
                                        aria-hidden="true"
                                        className="pointer-events-none absolute inset-0 rounded-[28px] overflow-hidden"
                                    >
                                        <div
                                            className="absolute inset-0"
                                            style={{
                                                background: `radial-gradient(420px circle at ${px * 100}% ${py * 100}%, rgba(255,255,255,0.10), transparent 55%)`,
                                                opacity: hover ? 1 : 0,
                                                transition: "opacity 250ms ease",
                                                mixBlendMode: "screen",
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* tagline strip */}
                                <div
                                    className="relative px-6 sm:px-10 pb-8"
                                    style={{ transform: "translateZ(20px)" }}
                                >
                                    <div className="shimmer-divider mb-5" />
                                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] sm:text-xs font-mono uppercase tracking-[0.22em] text-[#C9D3E1]">
                                        <span>Sauberkeit</span>
                                        <span className="text-[#2EA8FF]">·</span>
                                        <span>Qualität</span>
                                        <span className="text-[#2EA8FF]">·</span>
                                        <span>Zuverlässigkeit</span>
                                    </div>
                                </div>

                                {/* bottom reflection */}
                                <div
                                    aria-hidden="true"
                                    className="pointer-events-none absolute inset-x-8 -bottom-px h-px"
                                    style={{
                                        background:
                                            "linear-gradient(90deg, transparent, rgba(123,211,255,0.55), transparent)",
                                    }}
                                />
                            </div>

                            {/* floor reflection */}
                            <div
                                aria-hidden="true"
                                className="mx-10 mt-3 h-10 rounded-full"
                                style={{
                                    background:
                                        "radial-gradient(ellipse at center, rgba(46,168,255,0.18), transparent 70%)",
                                    filter: "blur(10px)",
                                    opacity: hover ? 1 : 0.6,
                                    transition: "opacity 400ms ease",
                                }}
                            />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
